import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { CalendarClock, FileText, TrendingUp, Clock, Users, Briefcase } from "lucide-react";

const money = (n) => "$" + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const iso = (d) => d.toISOString().slice(0, 10);

const DATE_PRESETS = [
  ["ytd", "Year to Date"], ["mtd", "This Month"], ["q", "Last 90 Days"], ["all", "All Time"],
];
function presetRange(key) {
  const t = new Date();
  if (key === "ytd") return [`${t.getFullYear()}-01-01`, iso(t)];
  if (key === "mtd") return [iso(new Date(t.getFullYear(), t.getMonth(), 1)), iso(t)];
  if (key === "q") { const s = new Date(t); s.setDate(s.getDate() - 90); return [iso(s), iso(t)]; }
  return ["", ""];
}

/* ---------------- shared widgets ---------------- */
const StatCard = ({ label, value, testid, onClick, accent }) => (
  <button type="button" data-testid={testid} onClick={onClick} disabled={!onClick}
    className={`text-left border rounded-sm p-4 bg-white transition-colors ${onClick ? "hover:bg-ice cursor-pointer nav-link" : "cursor-default"} ${accent ? "border-seared/40" : "border-border"}`}>
    <div className="text-[11px] uppercase tracking-wide text-mutedsea">{label}</div>
    <div className={`font-serif text-2xl mt-1 ${accent ? "text-seared" : "text-navy"}`}>{value}</div>
  </button>
);

const BarList = ({ title, rows, valueKey = "premium", fmt = money, testid, onRow }) => {
  const max = Math.max(1, ...rows.map((r) => Number(r[valueKey] || 0)));
  return (
    <div className="bg-white border border-border rounded-sm p-4" data-testid={testid}>
      <div className="text-xs uppercase tracking-wide text-navy font-semibold mb-3">{title}</div>
      {rows.length === 0 ? <div className="text-sm text-mutedsea py-2">No data.</div> : (
        <div className="space-y-2.5">
          {rows.map((r, i) => (
            <button key={i} type="button" onClick={onRow ? () => onRow(r) : undefined} disabled={!onRow}
              className={`w-full text-left ${onRow ? "cursor-pointer nav-link" : "cursor-default"}`}>
              <div className="flex justify-between text-sm mb-0.5">
                <span className="text-slate truncate pr-2">{r.name}{r.count != null && <span className="text-mutedsea"> · {r.count}</span>}</span>
                <span className="text-navy font-medium whitespace-nowrap">{fmt(r[valueKey])}{r.pct != null && <span className="text-mutedsea text-xs"> · {r.pct}%</span>}</span>
              </div>
              <div className="h-1.5 bg-cloud rounded-full overflow-hidden"><div className="h-full bg-atlantic rounded-full" style={{ width: `${Math.max(3, 100 * Number(r[valueKey] || 0) / max)}%` }} /></div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const MiniTable = ({ title, cols, rows, testid, empty = "No records." }) => (
  <div className="bg-white border border-border rounded-sm overflow-hidden" data-testid={testid}>
    <div className="px-4 py-3 border-b border-border text-xs uppercase tracking-wide text-navy font-semibold">{title}</div>
    <div className="overflow-x-auto end-scroll">
      <table className="w-full text-sm">
        <thead><tr className="bg-cloud border-b border-border">{cols.map((c) => <th key={c.k} className="text-left px-3 py-2 text-[11px] uppercase text-navy font-semibold whitespace-nowrap">{c.h}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 ? <tr><td colSpan={cols.length} className="px-3 py-5 text-center text-mutedsea">{empty}</td></tr> :
            rows.map((r, i) => <tr key={i} className="border-b border-border last:border-0 hover:bg-ice">{cols.map((c) => <td key={c.k} className="px-3 py-2 whitespace-nowrap">{c.render ? c.render(r) : r[c.k]}</td>)}</tr>)}
        </tbody>
      </table>
    </div>
  </div>
);

const DateFilter = ({ range, setRange }) => {
  const [preset, setPreset] = useState("ytd");
  const apply = (key) => { setPreset(key); const [f, t] = presetRange(key); setRange({ from: f, to: t }); };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="dashboard-date-filter">
      <Select value={preset} onValueChange={apply}>
        <SelectTrigger className="w-40 h-9" data-testid="date-preset"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-white">{DATE_PRESETS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          <SelectItem value="custom">Custom…</SelectItem></SelectContent>
      </Select>
      {preset === "custom" && <>
        <Input type="date" value={range.from} data-testid="date-from" onChange={(e) => setRange({ ...range, from: e.target.value })} className="h-9 w-40" />
        <span className="text-mutedsea text-sm">to</span>
        <Input type="date" value={range.to} data-testid="date-to" onChange={(e) => setRange({ ...range, to: e.target.value })} className="h-9 w-40" />
      </>}
    </div>
  );
};

const qstr = (range, extra = {}) => {
  const p = new URLSearchParams();
  if (range.from) p.set("eff_from", range.from);
  if (range.to) p.set("eff_to", range.to);
  Object.entries(extra).forEach(([k, v]) => { if (v) p.set(k, v); });
  return p.toString();
};

/* ---------------- PERSONAL ---------------- */
function PersonalTab({ range }) {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [recent, setRecent] = useState([]);
  const [acts, setActs] = useState([]);
  const [expiring, setExpiring] = useState(null);
  const [premChange, setPremChange] = useState(null);
  const [calDate, setCalDate] = useState(iso(new Date()));
  const [calEvents, setCalEvents] = useState([]);

  useEffect(() => { api.get(`/dashboard/personal?${qstr(range)}`).then(({ data }) => setD(data)).catch(() => toast.error("Load failed")); }, [range]);
  useEffect(() => {
    api.get("/agency/recent").then(({ data }) => setRecent((data.items || []).slice(0, 8))).catch(() => {});
    api.get("/activity-queue/list?view=today&mine=true&page_size=8").then(({ data }) => setActs(data.items || [])).catch(() => {});
    api.get("/agency/expiring-policies?bucket=30&page_size=5&producer=").then(({ data }) => setExpiring(data)).catch(() => {});
    api.get("/agency/reports/premium-change?page_size=5").then(({ data }) => setPremChange(data)).catch(() => {});
  }, []);
  useEffect(() => { api.get(`/agency/calendar?start=${calDate}&end=${calDate}&mine=true`).then(({ data }) => setCalEvents(data.events || [])).catch(() => {}); }, [calDate]);

  if (!d) return <div className="text-mutedsea py-6">Loading…</div>;
  const s = d.summary;
  return (
    <div className="space-y-4" data-testid="personal-tab">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Due Today" value={s.due_today} testid="personal-due-today" onClick={() => nav("/activities?view=today")} />
        <StatCard label="Overdue" value={s.overdue} accent={s.overdue > 0} testid="personal-overdue" onClick={() => nav("/activities?view=overdue")} />
        <StatCard label="Open Opportunities" value={s.open_opportunities} testid="personal-open-opps" onClick={() => nav("/opportunities")} />
        <StatCard label="Pipeline" value={money(s.pipeline)} testid="personal-pipeline" onClick={() => nav("/opportunities")} />
        <StatCard label="Calls / Meetings (MTD)" value={s.calls_meetings} testid="personal-calls" onClick={() => nav("/calendar")} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Daily Calendar */}
        <div className="bg-white border border-border rounded-sm overflow-hidden" data-testid="personal-calendar">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <span className="text-xs uppercase tracking-wide text-navy font-semibold flex items-center gap-1.5"><CalendarClock className="h-4 w-4 text-atlantic" /> Daily Calendar</span>
            <Input type="date" value={calDate} data-testid="personal-calendar-date" onChange={(e) => setCalDate(e.target.value)} className="h-8 w-40" />
          </div>
          <div className="divide-y divide-border max-h-64 overflow-y-auto end-scroll">
            {calEvents.length === 0 ? <div className="px-4 py-5 text-sm text-mutedsea">Nothing scheduled.</div> :
              calEvents.map((e) => (
                <button key={e.id} data-testid={`personal-cal-${e.record_id}`} onClick={() => nav(e.party_id ? `/clients/${e.party_id}` : `/${e.resource}`)} className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-ice nav-link">
                  <span className="text-[11px] px-1.5 py-0.5 rounded-sm border border-sky/40 bg-ice text-atlantic shrink-0">{e.type}</span>
                  <span className="text-sm text-slate truncate">{e.title}</span>
                </button>
              ))}
          </div>
        </div>

        {/* Activities / Messages */}
        <MiniTable title="Activities / Messages (Due Today)" testid="personal-activities"
          cols={[{ k: "title", h: "Subject", render: (r) => <button className="text-atlantic hover:underline nav-link" onClick={() => nav("/activities")}>{r.title}</button> },
            { k: "activity_type", h: "Type" }, { k: "due_date", h: "Due" }]}
          rows={acts} empty="No activities due today." />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Policies Expiring */}
        <MiniTable title={`Policies Expiring (Next 30 Days${expiring ? ` — ${money(expiring.premium_at_risk)} at risk` : ""})`} testid="personal-expiring"
          cols={[{ k: "client", h: "Client", render: (r) => <button className="text-atlantic hover:underline nav-link" onClick={() => nav(`/clients/${r.party_id}`)}>{r.client}</button> },
            { k: "carrier", h: "Carrier" }, { k: "expiration_date", h: "Expires" }, { k: "premium", h: "Premium", render: (r) => money(r.premium) }]}
          rows={(expiring?.items || [])} empty="No policies expiring soon." />

        {/* Premium Change */}
        <MiniTable title={`Premium Change${premChange ? ` — net ${money(premChange.summary?.total_change)}` : ""}`} testid="personal-premium-change"
          cols={[{ k: "client", h: "Client" }, { k: "policy_number", h: "Policy" },
            { k: "dollar_change", h: "Δ $", render: (r) => <span className={r.dollar_change >= 0 ? "text-seagreen" : "text-seared"}>{money(r.dollar_change)}</span> },
            { k: "percent_change", h: "Δ %", render: (r) => r.percent_change != null ? `${r.percent_change}%` : "—" }]}
          rows={(premChange?.items || [])} empty="No comparable terms yet." />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* New Business */}
        <MiniTable title={`New Business (${d.window.from} → ${d.window.to}) — ${money(s.new_business_premium)}`} testid="personal-new-business"
          cols={[{ k: "client", h: "Client", render: (r) => <button className="text-atlantic hover:underline nav-link" onClick={() => nav(`/clients/${r.party_id}`)}>{r.client}</button> },
            { k: "line", h: "LOB" }, { k: "carrier", h: "Carrier" }, { k: "effective_date", h: "Effective" }, { k: "premium", h: "Premium", render: (r) => money(r.premium) }]}
          rows={d.new_business} empty="No new business in this period." />

        {/* Recently Accessed */}
        <div className="bg-white border border-border rounded-sm overflow-hidden" data-testid="personal-recent">
          <div className="px-4 py-3 border-b border-border text-xs uppercase tracking-wide text-navy font-semibold flex items-center gap-1.5"><Clock className="h-4 w-4 text-atlantic" /> Recently Accessed</div>
          <div className="divide-y divide-border max-h-64 overflow-y-auto end-scroll">
            {recent.length === 0 ? <div className="px-4 py-5 text-sm text-mutedsea">Nothing recently viewed.</div> :
              recent.map((r, i) => (
                <button key={i} data-testid={`personal-recent-${i}`} onClick={() => nav(r.party_id ? `/clients/${r.party_id}` : `/${r.resource}`)} className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-ice nav-link">
                  <span className="text-[11px] px-1.5 py-0.5 rounded-sm border border-border bg-cloud text-mutedsea shrink-0 capitalize">{(r.resource || "").replace(/_/g, " ")}</span>
                  <span className="text-sm text-slate truncate">{r.label || r.record_id}</span>
                </button>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- SALES (Agency & Producer share) ---------------- */
function SalesPerspective({ m, nav, testidPrefix, showProducerBreakdown }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Open Submissions" value={m.open_submissions} testid={`${testidPrefix}-open-subs`} onClick={() => nav("/opportunities")} />
        <StatCard label="Total New Business" value={m.total_new_business} testid={`${testidPrefix}-total-nb`} onClick={() => nav("/policies")} />
        <StatCard label="New Business Premium" value={money(m.new_business_premium)} testid={`${testidPrefix}-nb-prem`} onClick={() => nav("/policies")} />
        <StatCard label="Win / Loss Ratio YTD" value={m.win_loss_ratio_ytd != null ? `${m.win_loss_ratio_ytd}%` : "—"} testid={`${testidPrefix}-winloss`} onClick={() => nav("/opportunities")} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BarList title="Open Submissions by Carrier" rows={m.open_submissions_by_carrier} valueKey="count" fmt={(v) => v} testid={`${testidPrefix}-subs-by-carrier`} onRow={() => nav("/opportunities")} />
        <BarList title="Submissions by Sales Stage" rows={m.submissions_by_sales_stage} valueKey="count" fmt={(v) => v} testid={`${testidPrefix}-subs-by-stage`} onRow={() => nav("/opportunities")} />
        {showProducerBreakdown
          ? <BarList title="New Business by Producer" rows={m.new_business_by_producer} testid={`${testidPrefix}-nb-by-producer`} onRow={() => nav("/policies")} />
          : <BarList title="New Business by Carrier YTD" rows={m.new_business_by_carrier || []} testid={`${testidPrefix}-nb-by-carrier`} onRow={() => nav("/policies")} />}
        <MiniTable title="Top Prospects (by open pipeline)" testid={`${testidPrefix}-top-prospects`}
          cols={[{ k: "name", h: "Prospect", render: (r) => <button className="text-atlantic hover:underline nav-link" onClick={() => nav(`/clients/${r.party_id}`)}>{r.name}</button> },
            { k: "opportunities", h: "Opps" }, { k: "pipeline", h: "Pipeline", render: (r) => money(r.pipeline) }]}
          rows={m.top_prospects} empty="No open prospect pipeline." />
      </div>
    </div>
  );
}

function AgencyTab({ range }) {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [producer, setProducer] = useState("");
  const load = useCallback(() => { api.get(`/dashboard/agency?${qstr(range, { producer })}`).then(({ data }) => setD(data)).catch(() => toast.error("Load failed")); }, [range, producer]);
  useEffect(load, [load]);
  if (!d) return <div className="text-mutedsea py-6">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="agency-tab">
      <div className="flex justify-end">
        <Select value={producer || "all"} onValueChange={(v) => setProducer(v === "all" ? "" : v)}>
          <SelectTrigger className="w-56 h-9" data-testid="agency-producer-picker"><SelectValue placeholder="Whole Agency" /></SelectTrigger>
          <SelectContent className="bg-white max-h-72"><SelectItem value="all">Whole Agency</SelectItem>
            {(d.producers || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <SalesPerspective m={d.metrics} nav={nav} testidPrefix="agency" showProducerBreakdown />
    </div>
  );
}

function ProducerTab({ range }) {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [pid, setPid] = useState("");
  const load = useCallback(() => { api.get(`/dashboard/producer?${qstr(range, { producer_user_id: pid })}`).then(({ data }) => setD(data)).catch(() => toast.error("Load failed")); }, [range, pid]);
  useEffect(load, [load]);
  if (!d) return <div className="text-mutedsea py-6">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="producer-tab">
      <div className="flex items-center justify-between">
        <div className="text-sm text-mutedsea">Showing: <span className="text-slate font-medium">{d.producer_name}</span></div>
        {d.is_admin && (
          <Select value={pid || d.producer_user_id} onValueChange={setPid}>
            <SelectTrigger className="w-56 h-9" data-testid="producer-picker"><SelectValue placeholder="Select producer" /></SelectTrigger>
            <SelectContent className="bg-white max-h-72">{(d.producers || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>
      <SalesPerspective m={d.metrics} nav={nav} testidPrefix="producer" showProducerBreakdown={false} />
    </div>
  );
}

/* ---------------- BOOK OF BUSINESS ---------------- */
function BookTab({ range }) {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [producer, setProducer] = useState("");
  const load = useCallback(() => { api.get(`/dashboard/book?${qstr(range, { producer })}`).then(({ data }) => setD(data)).catch(() => toast.error("Load failed")); }, [range, producer]);
  useEffect(load, [load]);
  if (!d) return <div className="text-mutedsea py-6">Loading…</div>;
  const m = d.metrics;
  const ep = m.expiring_premium || {};
  return (
    <div className="space-y-4" data-testid="book-tab">
      {d.is_admin && (
        <div className="flex justify-end">
          <Select value={producer || "all"} onValueChange={(v) => setProducer(v === "all" ? "" : v)}>
            <SelectTrigger className="w-56 h-9" data-testid="book-producer-picker"><SelectValue placeholder="Whole Agency" /></SelectTrigger>
            <SelectContent className="bg-white max-h-72"><SelectItem value="all">Whole Agency</SelectItem>
              {(d.producers || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Active Policies" value={m.active_policies} testid="book-active-policies" onClick={() => nav("/policies")} />
        <StatCard label="Clients w/ Active Policies" value={m.clients_active} testid="book-clients-active" onClick={() => nav("/clients")} />
        <StatCard label="Active Written Premium" value={money(m.active_written_premium)} testid="book-active-premium" onClick={() => nav("/book")} />
        <StatCard label="Retention" value={m.retention != null ? `${m.retention}%` : "—"} testid="book-retention" onClick={() => nav("/renewals")} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Lost Business YTD" value={m.lost_business_count} accent={m.lost_business_count > 0} testid="book-lost-count" onClick={() => nav("/policies")} />
        <StatCard label="Lost Premium YTD" value={money(m.lost_business_premium)} testid="book-lost-prem" />
        <StatCard label="Expiring 30d" value={money(ep.d30)} testid="book-exp30" onClick={() => nav("/expiring-policies")} />
        <StatCard label="Expiring 90d" value={money(ep.d90)} testid="book-exp90" onClick={() => nav("/expiring-policies")} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BarList title="Carrier Concentration (Active Premium)" rows={m.carrier_concentration} testid="book-carrier-concentration" onRow={() => nav("/book")} />
        <BarList title="Line of Business Mix (Active Premium)" rows={m.lob_mix} testid="book-lob-mix" onRow={() => nav("/book")} />
        <BarList title="Active Policies by Carrier" rows={m.active_by_carrier} valueKey="count" fmt={(v) => v} testid="book-active-by-carrier" onRow={() => nav("/policies")} />
        <BarList title="Clients w/ Active Policies by LOB" rows={m.clients_by_lob} valueKey="count" fmt={(v) => v} testid="book-clients-by-lob" onRow={() => nav("/clients")} />
        <BarList title="New Business by Producer" rows={m.new_business_by_producer} testid="book-nb-by-producer" onRow={() => nav("/policies")} />
        <BarList title="Cancelled Policies by Month YTD" rows={m.cancelled_by_month} valueKey="count" fmt={(v) => v} testid="book-cancelled-by-month" />
      </div>
    </div>
  );
}

/* ---------------- PAGE ---------------- */
export default function Dashboard() {
  const { level } = useAuth();
  const isAdmin = level >= 80;
  const [tab, setTab] = useState("personal");
  const [range, setRange] = useState({ from: presetRange("ytd")[0], to: presetRange("ytd")[1] });

  return (
    <div className="p-6" data-testid="dashboard-page">
      <PageHeader title="Dashboard" subtitle="Consolidated management analytics — personal, sales production and portfolio.">
        <DateFilter range={range} setRange={setRange} />
      </PageHeader>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4" data-testid="dashboard-tabs">
          <TabsTrigger value="personal" data-testid="tab-personal"><Users className="h-4 w-4 mr-1.5" /> Personal</TabsTrigger>
          {isAdmin && <TabsTrigger value="agency" data-testid="tab-agency"><Briefcase className="h-4 w-4 mr-1.5" /> Agency</TabsTrigger>}
          <TabsTrigger value="producer" data-testid="tab-producer"><TrendingUp className="h-4 w-4 mr-1.5" /> Producer</TabsTrigger>
          <TabsTrigger value="book" data-testid="tab-book"><FileText className="h-4 w-4 mr-1.5" /> Book of Business</TabsTrigger>
        </TabsList>
        <TabsContent value="personal">{tab === "personal" && <PersonalTab range={range} />}</TabsContent>
        {isAdmin && <TabsContent value="agency">{tab === "agency" && <AgencyTab range={range} />}</TabsContent>}
        <TabsContent value="producer">{tab === "producer" && <ProducerTab range={range} />}</TabsContent>
        <TabsContent value="book">{tab === "book" && <BookTab range={range} />}</TabsContent>
      </Tabs>
    </div>
  );
}
